import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Paper, Typography } from '@mui/material';
import { Apps, BarChart as BarChartIcon, Palette, PhotoLibrary } from '@mui/icons-material';
import ThemeList from '../Theme/ThemeList';
import { useI18n } from '../../i18n/useI18n';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { isStaffUser } from '../../utils/staffUser';
import * as api from '../../services/api';
import LiteGalleryGrid from '../shared/LiteGalleryGrid';
import MPhotoSectionTabs from '../shared/MPhotoSectionTabs';
import GalleryStatsPanel from '../shared/GalleryStatsPanel';
import UserPageShell from '../shared/UserPageShell';
import UserIcons from './Icons';

type LiteTab = 'gallery' | 'icons' | 'themes' | 'stats';

const LITE_PINK_DEEP = '#e86a8a';

const MPhotoLite: React.FC = () => {
  const { pages: t } = useI18n();
  const { user } = useSelector((state: RootState) => state.auth);
  const readOnly = isStaffUser(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const rawTab: LiteTab =
    tabParam === 'icons'
      ? 'icons'
      : tabParam === 'themes' || tabParam === 'custom-frames'
        ? 'themes'
        : tabParam === 'stats'
          ? 'stats'
          : 'gallery';
  const activeTab: LiteTab = readOnly ? 'gallery' : rawTab;

  const [mobileUploadEnabled, setMobileUploadEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getMobileUploadStatus();
        if (!cancelled) setMobileUploadEnabled(res.mobileUploadEnabled === true);
      } catch {
        if (!cancelled) setMobileUploadEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setActiveTab = (tab: LiteTab) => {
    if (tab === 'gallery') {
      searchParams.delete('tab');
      setSearchParams(searchParams, { replace: true });
    } else {
      searchParams.set('tab', tab);
      setSearchParams(searchParams, { replace: true });
    }
  };

  const tabOptions = useMemo(() => {
    if (readOnly) {
      return [
        { value: 'gallery' as const, label: t.mPhotoLite.tabs.gallery, icon: <PhotoLibrary fontSize="inherit" /> },
      ];
    }
    const opts: { value: LiteTab; label: string; shortLabel?: string; icon: React.ReactNode }[] = [
      { value: 'gallery', label: t.mPhotoLite.tabs.gallery, icon: <PhotoLibrary fontSize="inherit" /> },
      { value: 'themes', label: t.mPhotoLite.tabs.themes, shortLabel: t.mPhotoLite.tabs.themesShort, icon: <Palette fontSize="inherit" /> },
    ];
    if (mobileUploadEnabled) {
      opts.push({ value: 'icons', label: t.mPhotoLite.tabs.icons, icon: <Apps fontSize="inherit" /> });
    }
    opts.push({ value: 'stats', label: t.mPhotoLite.tabs.stats, icon: <BarChartIcon fontSize="inherit" /> });
    return opts;
  }, [mobileUploadEnabled, t, readOnly]);

  useEffect(() => {
    if (mobileUploadEnabled === false && tabParam === 'icons') {
      searchParams.delete('tab');
      setSearchParams(searchParams, { replace: true });
    }
  }, [mobileUploadEnabled, tabParam, searchParams, setSearchParams]);

  return (
    <UserPageShell enter>
      <Typography
        variant="h5"
        component="h1"
        sx={{
          textAlign: 'center',
          fontWeight: 800,
          fontSize: { xs: '1.35rem', sm: '1.75rem' },
          lineHeight: 1.2,
          color: LITE_PINK_DEEP,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          mb: { xs: 1.5, md: 2.5 },
        }}
      >
        Lite
      </Typography>

      {tabOptions.length > 1 && (
        <MPhotoSectionTabs
          value={activeTab}
          onChange={setActiveTab}
          options={tabOptions}
          accentColor={LITE_PINK_DEEP}
          size="sm"
          sticky
          stickyTop={{ xs: 56, sm: 64 }}
        />
      )}

      {activeTab === 'gallery' && (
        <LiteGalleryGrid
          fetchPage={api.getLitePhotoGroups}
          purgeExpired={() => api.purgeExpiredLitePhotoGroups()}
          purgeExpiredKey="user:lite"
          onDelete={readOnly ? undefined : api.deleteLitePhotoGroup}
          showDelete={!readOnly}
          emptyMessage={t.mPhotoLite.galleryEmpty}
          deviceProduct="lite"
          loadMachines={async () => {
            const res = await api.getUserMachines();
            return res.machines || [];
          }}
        />
      )}

      {!readOnly && activeTab === 'themes' && <ThemeList channel="lite" embedded />}

      {!readOnly && activeTab === 'icons' && mobileUploadEnabled && <UserIcons channel="lite" embedded />}

      {!readOnly && activeTab === 'icons' && mobileUploadEnabled === false && (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography color="text.secondary">{t.mPhotoLite.iconsDisabled}</Typography>
        </Paper>
      )}

      {!readOnly && activeTab === 'stats' && <GalleryStatsPanel channel="lite" accentColor={LITE_PINK_DEEP} />}
    </UserPageShell>
  );
};

export default MPhotoLite;
